import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Clock, Star, MapPin, Car, Users, Phone, ChevronRight, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";
import BookingForm from "@/components/BookingForm";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const heroImages = [
  "/WhatsApp Image 2026-03-30 at 10.47.45 AM.jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM.jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (9).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (8).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (7).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (5).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (6).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (4).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (3).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (2).jpeg",
  "/WhatsApp Image 2026-03-30 at 10.47.44 AM (1).jpeg"
];

const whyChooseUs = [
  { icon: Shield, title: "Safe & Verified Drivers", desc: "All our drivers are background-verified, trained, and experienced in navigating Pondicherry's roads safely." },
  { icon: Star, title: "Premium Service", desc: "Experience the highest standard of travel with our well-maintained fleet and professional chauffeurs." },
  { icon: Clock, title: "24/7 Availability", desc: "Book a cab anytime — early morning airport runs or late-night rides across Puducherry, we're always available." },
  { icon: Users, title: "Group Travel", desc: "Comfortable vehicles for family trips, corporate events, and group sightseeing in Puducherry." },
];

const routes = [
  { city: "Chennai", distance: "150 km", time: "3 hrs", path: "/routes/pondicherry-to-chennai" },
  { city: "Bangalore", distance: "310 km", time: "6 hrs", path: "/routes/pondicherry-to-bangalore" },
  { city: "Tirupati", distance: "280 km", time: "5.5 hrs", path: "/routes/pondicherry-to-tirupati" },
  { city: "Madurai", distance: "310 km", time: "5.5 hrs", path: "/routes/pondicherry-to-madurai" },
  { city: "Pichavaram", distance: "80 km", time: "2 hrs", path: "/routes/pondicherry-to-pichavaram" },
  { city: "Thiruvannamalai", distance: "110 km", time: "2.5 hrs", path: "/routes/pondicherry-to-thiruvannamalai" },
  { city: "Mahabalipuram", distance: "100 km", time: "2 hrs", path: "/routes/pondicherry-to-mahabalipuram" },
];

const cars = [
  { name: "Sedan", desc: "Swift Dzire, Etios", capacity: "4+1 Seater", features: ["AC", "Spacious Boot", "8hrs/80km", "Professional Driver"], popular: true },
  { name: "SUV", desc: "Xylo, Tavera", capacity: "6/7+1 Seater", features: ["AC", "Extra Luggage", "8hrs/80km", "Rugged & Safe"] },
  { name: "MUV", desc: "Innova Crysta – Premium Innova Cab Booking Pondicherry", capacity: "7+1 Seater", features: ["High-End AC", "Great Comfort", "8hrs/80km", "Captain Seats"] },
  { name: "Tempo Traveller", desc: "Force Traveler – Tempo Traveller Hire Pondicherry", capacity: "14/17+1 Seater", features: ["Pushback Seats", "Large Group", "AC Luxury", "Perfect for Tours"] },
];

const testimonials = [
  { name: "Rahul Sharma", role: "Tourist from Delhi", text: "Excellent service! The driver was punctual and the car was clean. Best taxi experience in Pondicherry.", rating: 5 },
  { name: "Priya Nair", role: "Business Traveler", text: "I regularly use Pondicherry Taxi for my Chennai trips. Always reliable, always on time. Highly recommended!", rating: 5 },
  { name: "Jean-Pierre Moreau", role: "French Tourist", text: "We explored all of Pondicherry and Auroville with their taxi service. The driver knew all the best spots. Merci!", rating: 5 },
  { name: "Anitha Krishnan", role: "Local Resident", text: "Affordable and trustworthy. I've been using their service for airport pickups for 2 years now. Never disappointed.", rating: 5 },
];

const faqs = [
  { q: "How do I book a taxi in Pondicherry?", a: "You can book a taxi by calling us at +91 99524 93907, messaging us on WhatsApp, or using the booking form on our website. We are a 24 hour taxi service in Pondicherry — available for early morning airport runs, late night rides, and emergency travel. We confirm your booking within minutes." },
  { q: "What is the fare for Pondicherry to Chennai taxi?", a: "We offer the best service for Pondicherry to Chennai airport transfers and outstation trips. You can choose from various car types based on your comfort and group size." },
  { q: "Do you provide airport pickup and drop service?", a: "Yes, we provide airport pickup and drop services from Chennai International Airport and Pondicherry Airport. Our drivers track your flight for timely pickup." },
  { q: "Are your drivers verified and safe?", a: "Absolutely. All our drivers undergo thorough background verification, are professionally trained, and have years of experience driving in and around Pondicherry." },
  { q: "Can I book a taxi for a full day in Pondicherry?", a: "Yes! We offer hourly rental packages starting from 4 hours. Perfect for sightseeing in Pondicherry, visiting Auroville, or exploring nearby attractions." },
  { q: "Do you offer outstation taxi from Pondicherry?", a: "Yes, we offer outstation taxi services including Pondicherry to Bangalore one way cab, Chennai, Tirupati, Madurai, and many more destinations. Our one-way drop service means you only pay for the distance you travel — no return fare charged." },
];

const Index = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Best Pondicherry Taxi Service | Reliable Local & Outstation Cabs"
        description="Book the best taxi service in Pondicherry for local sightseeing, airport transfers, and outstation trips. Reliable and affordable Pondicherry taxi service with 24/7 availability."
        keywords="Pondicherry taxi service, taxi in pondicherry, cab booking pondicherry, pondicherry to chennai taxi, pondicherry airport taxi, outstation taxi pondicherry, best taxi in pondicherry, pondy taxi connect, pondicherry sightseeing taxi, puducherry cab service, best taxi services in pondicherry, taxi services in pondicherry, 24/7 taxi services, Affordable Taxi Services in Pondicherry, Affordable Taxi Fares in Pondicherry, Pondicherry local sightseeing taxi, Pondicherry city tour packages, Pondicherry sightseeing cab service, Pondicherry airport taxi service, Airport pickup and drop Pondicherry, Chennai to Pondicherry airport taxi, Pondicherry to Chennai cab, Daily pickup drop service, Office pickup drop taxi, Chennai Airport drop taxi service, Pondicherry Airport drop Taxi service, Tours and travels in pondicherry, One-way drop taxi service in pondicherry, Local Pondicherry sightseeing(City Tour), Airport transfer (Hotels)pondicherry, Outstation trips pondicherry, corporate cab service in pondicherry, Cab service in pondicherry, Call Taxi service in pondicherry, Taxi service for Outstation, One way drop taxi service in pondicherry, Best Taxi service in pondicherry, Taxi service in pondicherry Pondicherry City tour package, Pondicherry Airport Transfer pickup and drop taxi service,Pondicherry taxi service, book taxi online in Pondicherry, Pondicherry to Chennai airport taxi, one way taxi booking Pondicherry, 24 hour taxi service Pondicherry"
        canonical="https://pondicherry.taxi/"
      />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Slider */}
        <div className="absolute inset-0 z-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 1.2 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.5, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <img
                src={heroImages[currentImageIndex]}
                alt="Pondicherry Taxi Hero"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>
          {/* Overlays - Ultra light to show images clearly */}
          <div className="absolute inset-0 bg-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 container-custom pt-32 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-block px-0 py-2 text-xs font-black text-primary mb-6 uppercase tracking-widest drop-shadow-sm"
            >
              🚗 #1 Rated Taxi & Cab Near Me in Puducherry
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.1] mb-8 text-white drop-shadow-lg"
            >
              Best <span className="text-gradient">Pondicherry Taxi Service</span> – Reliable & Affordable Cabs
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="text-lg sm:text-xl text-white/95 max-w-xl mb-12 leading-relaxed font-bold drop-shadow-lg"
            >
              Looking for a reliable taxi in Pondicherry or a cab near me? Book premium cabs for local sightseeing, airport transfers, and outstation trips.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="flex flex-wrap gap-5"
            >
              <a href="#booking" className="btn-primary px-10 py-5 text-lg font-black shadow-none active:scale-95 transition-transform">
                Book Your Ride
              </a>
              <a href="tel:+919952493907" className="px-10 py-5 flex items-center gap-3 text-lg font-bold text-white hover:text-primary transition-colors active:scale-95 drop-shadow-md">
                <Phone className="w-6 h-6" /> Call Now
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="lg:ml-auto w-full max-w-[320px]"
          >
            <BookingForm minimal idPrefix="hero" className="bg-white/60 backdrop-blur-xl p-8 rounded-[3rem]" />
          </motion.div>
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentImageIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-12 h-1.5 rounded-full transition-all duration-500 ${currentImageIndex === i ? "bg-primary w-20" : "bg-white/30 hover:bg-white/50"
                }`}
            />
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="container-custom">
          <SectionHeading
            badge="Why Choose Us"
            title="Pondicherry's #1 Most"
            highlight="Trusted Taxi Service"
            description="We are the leading provider of reliable cab service in Puducherry, offering safe, affordable, and punctual rides for all your travel needs in Pondicherry."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, i) => (
              <motion.div
                key={item.title}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className="glass-card-hover rounded-2xl p-6 text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-foreground font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE SERVICES LIST */}
      <section className="py-16 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="glass-card-hover rounded-[3rem] p-8 sm:p-12 border border-white/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <SectionHeading
                  badge="Our Services"
                  title="Professional & Reliable"
                  highlight="Taxi Services in Pondy"
                  description="From city tours to Pondicherry airport taxi service and outstation trips, we offer a comprehensive range of cab services in Puducherry tailored to your budget and comfort."
                  align="left"
                />
                <div className="space-y-4 mt-8">
                  {[
                    "Local Pondicherry sightseeing (City Tour)",
                    "Airport transfer (Hotels)",
                    "Outstation trips",
                    "One way drop taxi services (Pick up (or) drop)",
                    "Corporate cab service"
                  ].map((service, i) => (
                    <motion.div
                      key={i}
                      {...fadeUp}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-4 text-lg font-bold text-white group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary border border-primary/30 group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                        {i + 1}
                      </div>
                      {service}
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-3xl opacity-20 animate-pulse" />
                  <img
                    src="/standard_innova_suv_1774520923131.png"
                    alt="Pondicherry Taxi Services"
                    className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR ROUTES */}
      <section className="section-padding bg-taxi-darker/50">
        <div className="container-custom">
          <SectionHeading
            badge="Popular Routes"
            title="Book Outstation Taxi from"
            highlight="Pondicherry"
            description="Travel comfortably with our outstation cab from Pondicherry to Chennai, Bangalore, Tirupati, and other major cities. Reliable, affordable, and available 24/7."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {routes.map((r, i) => (
              <motion.div key={r.city} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <Link to={r.path} className="glass-card-hover rounded-2xl p-6 block group">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Pondicherry → {r.city}</h3>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>Distance: {r.distance}</p>
                    <p>Travel Time: {r.time}</p>
                  </div>
                  <span className="mt-4 inline-flex items-center text-sm text-primary font-medium group-hover:gap-2 transition-all">
                    View Details <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAR CATEGORIES */}
      <section className="section-padding">
        <div className="container-custom">
          <SectionHeading
            badge="Our Fleet"
            title="Top-Rated Pondicherry"
            highlight="Taxi Fleet Options"
            description="Choose from our wide range of well-maintained vehicles, including Sedans, SUVs, and Tempo Travellers, for your perfect Pondicherry taxi ride."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cars.map((car, i) => (
              <motion.div
                key={car.name}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className={`glass-card-hover rounded-2xl p-6 relative ${car.popular ? "border-primary/40 ring-1 ring-primary/20" : ""}`}
              >
                {car.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                    Most Popular
                  </span>
                )}
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                  <Car className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground">{car.name}</h3>
                <p className="text-[10px] text-muted-foreground mb-1">{car.desc}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
                  <Users className="w-3 h-3" /> {car.capacity}
                </div>
                <ul className="space-y-1.5 mb-6">
                  {car.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <a href="tel:+919952493907" className="btn-primary w-full text-center block text-sm py-2">
                  Book {car.name}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SIGHTSEEING SPOTS */}
      <section className="section-padding relative overflow-hidden">
        <div className="container-custom">
          <SectionHeading
            badge="Sightseeing"
            title="Top Pondicherry Sightseeing"
            highlight="Cab Service"
            description="Book a Pondicherry sightseeing cab and discover the beauty of Puducherry. Our expert drivers take you to the best tourist spots comfortably, safely, and at the best price."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              "Sri Aurobindho Ashram",
              "Sri Ganapathi Temple",
              "Pondicherry (Govt) Museum",
              "French war Memorial",
              "Promanade (Rock) Beach",
              "Auroville Matrimandhir (Golden Globe Outerveiw)",
              "Auro Beach",
              "Pondy Marina Mangrove Forest (Boat Trip)",
              "Live Art Museum",
              "Pondy Marina Eco Beach & Light House",
              "Botanical Garden"
            ].map((spot, i) => (
              <motion.div
                key={spot}
                {...fadeUp}
                transition={{ delay: i * 0.05 }}
                className="glass-card-hover p-6 rounded-2xl flex items-center gap-4 transition-shadow group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors overflow-hidden">
                  {i + 1}
                </div>
                <span className="font-semibold text-foreground">{spot}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-padding bg-gray-900 text-white rounded-[4rem] mx-4 overflow-hidden">
        <div className="container-custom">
          <SectionHeading badge="Testimonials" title="What Our Customers" highlight="Say About Us" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ delay: i * 0.1 }} className="bg-white/5 border border-white/10 rounded-[3rem] p-10 backdrop-blur-sm">
                <div className="flex gap-1.5 mb-6">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-gray-300 text-lg sm:text-xl italic mb-10 leading-relaxed font-light">"{t.text}"</p>
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center text-primary font-black text-xl">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{t.name}</p>
                    <p className="text-gray-500 text-sm font-medium">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="booking" className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="bg-taxi-darker border border-white/10 rounded-[3rem] shadow-2xl p-8 sm:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <SectionHeading badge="Quick Booking" title="Book Your" highlight="Pondicherry Taxi" description="Fill in the details below and we'll confirm your ride instantly. Safe, reliable, and affordable." />
            <div className="max-w-2xl mx-auto">
              <BookingForm idPrefix="main" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background">
        <div className="container-custom max-w-3xl">
          <SectionHeading badge="FAQ" title="Frequently Asked" highlight="Questions" />
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.details
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.05 }}
                className="bg-white/5 rounded-2xl shadow-sm hover:shadow-md transition-all border border-white/10 hover:border-primary/20 group"
              >
                <summary className="cursor-pointer p-6 text-white font-bold text-base list-none flex items-center justify-between">
                  {faq.q}
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center transition-colors group-open:bg-primary group-open:text-primary-foreground">
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  </div>
                </summary>
                <p className="px-6 pb-6 text-gray-400 leading-relaxed font-medium">{faq.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <article className="prose prose-invert prose-slate max-w-none prose-headings:text-white prose-p:text-gray-300 prose-strong:text-primary">
            <h2 className="text-3xl font-black mb-8 underline decoration-primary/30 decoration-8 underline-offset-8 text-white">Best Taxi Service in Pondicherry – Your Ultimate Travel Partner</h2>

            <p>Looking for a reliable and affordable <strong>taxi service in Pondicherry</strong> or searching <strong>taxi near me in Puducherry</strong>? Pondicherry Taxi is your premier choice for cab booking in Puducherry, offering high-quality, safe, and comfortable rides for local sightseeing, airport transfers, and long-distance outstation travel. Whether you need a <strong>cab in White Town Pondicherry</strong>, a shuttle to <strong>Auroville</strong>, or a <strong>Pondicherry to Chennai taxi</strong> at the best price, we are here to serve you with our professional chauffeurs and well-maintained fleet.</p>

            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">Why Book with the Best Taxi in Pondicherry?</h3>
            <p>Pondicherry (Puducherry) is a top tourist destination in India, known for its French heritage, beautiful beaches, and spiritual aura. To truly enjoy your visit, you need a dependable <strong>Pondicherry cab service</strong>. Our <strong>Pondicherry Taxi</strong> company stands out by providing transparent pricing, 24/7 customer support, and experienced local drivers who know every hidden gem in the city.</p>

            <p>Our fleet is diverse, ensuring we have the right vehicle for every need. From budget-friendly <strong>Sedans</strong> like Swift Dzire for small families to luxury <strong>Innova Crysta</strong> for premium comfort, and even large <strong>Tempo Travellers</strong> for group tours, we provide the best taxi in Pondicherry for all occasions.</p>

            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">Local Sightseeing & Outstation Cab Packages</h3>
            <p>Explore the French Quarter, Paradise Beach, and Rock Beach with our <strong>local taxi service in Pondicherry</strong>. We offer flexible hourly rentals and full-day sightseeing packages. For those looking to travel beyond, our <strong>outstation taxi from Pondicherry</strong> connects you to Chennai, Bangalore, Tirupati, Madurai, and more. Our <strong>one-way drop taxi</strong> service is perfect for travelers looking for cost-effective travel without paying for a round trip.</p>

            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">Pondicherry to Chennai Airport Taxi</h3>
            <p>We specialize in <strong>Chennai airport transfers from Pondicherry</strong>. Arrive on time and in style with our 24/7 airport cab service. We monitor your flight schedule to ensure timely pickups, making your journey between Pondicherry and Chennai International Airport seamless and stress-free.</p>
            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">More Routes & Services We Cover</h3>
            <p>
              Beyond Chennai, we are the go-to choice for a <strong>Pondicherry to Bangalore one way cab</strong> — pay only for the distance you travel.
              Pilgrims and tourists also book us for <strong>Pondicherry to Tiruvannamalai taxi</strong> rides, especially during Karthigai Deepam season.
              Heading to the Shore Temple? Our <strong>Pondicherry to Mahabalipuram cab</strong> service is available daily with experienced drivers.
              Need a large vehicle? We offer <strong>tempo traveller hire in Pondicherry</strong> for school trips, corporate events, and group pilgrimages.
              For luxury comfort, book an <strong>Innova cab in Pondicherry</strong> with captain seats and premium AC.
              And as a true <strong>24 hour taxi service in Pondicherry</strong>, we are available round the clock — no matter when your journey begins.
            </p>

            <p>Ready for a smooth ride? Book your <strong>Pondicherry taxi online</strong> today and experience the difference with the most trusted cab service in Puducherry! We offer the premier <strong>Cab service in pondicherry</strong> and <strong>Call Taxi service in pondicherry</strong>. Whether you need a <strong>Taxi service for Outstation</strong>, a reliable <strong>One way drop taxi service in pondicherry</strong>, or a comprehensive <strong>Taxi service in pondicherry Pondicherry City tour package</strong>, we are proud to be recognized as the <strong>Best Taxi service in pondicherry</strong>. Don't forget our specialized <strong>Pondicherry Airport Transfer pickup and drop taxi service</strong> for all your airport transit needs.</p>
          </article>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
